import { Check } from 'lucide-react';
import { useHabitStore } from '@/stores/habitStore';
import { useHabitLog } from '@/features/habits/useHabitLog';
import { useLast7Days } from '@/features/habits/useHabits';
import { cn } from '@/lib/cn';
import { habitColor } from '@/lib/habitColor';

export interface HabitGrid7Props {
  habitId: string;
  /** Override "now" for tests. */
  now?: Date;
  className?: string;
}

export function HabitGrid7({ habitId, now, className }: HabitGrid7Props) {
  const reference = now ?? new Date();
  const cells = useLast7Days(habitId, reference);
  const habit = useHabitStore((s) => s.habits.find((h) => h.id === habitId));
  const { toggleDay } = useHabitLog();

  if (!habit) return null;

  return (
    <ol className={cn('grid grid-cols-7 gap-1.5', className)}>
      {cells.map((cell) => (
        <li key={cell.iso}>
          <button
            type="button"
            aria-label={`${cell.label} — ${cell.done ? 'done' : 'not done'}. Tap to toggle.`}
            aria-pressed={cell.done}
            aria-current={cell.isToday ? 'date' : undefined}
            onClick={() => toggleDay(habit, cell.iso)}
            style={cell.done ? { backgroundColor: habitColor(habit.color) } : undefined}
            className={cn(
              'flex aspect-square w-full items-center justify-center rounded-md min-h-11 md:min-h-9 border',
              'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
              'motion-reduce:transition-none',
              // v4: empty days are quiet hairline cells (paper), not filled
              // blocks — the color on this page belongs to done days only.
              cell.done ? 'border-transparent' : 'border-line-2 bg-canvas',
              cell.isToday && 'ring-2 ring-brand ring-offset-1 ring-offset-canvas',
            )}
          >
            {cell.done && <Check className="h-4 w-4 text-white" strokeWidth={3} aria-hidden />}
          </button>
          <span className="block text-center text-2xs text-t2 mt-1">{cell.dow}</span>
        </li>
      ))}
    </ol>
  );
}
